import { put, call, takeLatest } from 'redux-saga/effects';
import { getExceptionMessage } from '../../../helpers/utils/utils';
import { DASHBOARD_ACOES } from '../../../helpers/constants/dashboard.constants';
import {
  pesquisarProcessosUnificadaService,
  pesquisarMeusProcessos,
  pesquisarPromessasBoletos,
  pesquisarAcordosNaoCumpridos,
  pesquisarPrazos,
  pesquisarPromessasRetorno,
  pesquisarProcessosNovos,
  pesquisarProcessosAVencer,
  pesquisarProcessosComPrazoVencido,
  pesquisarProcessosAguardandoRequisicao,
  pesquisarProcessosProximoVencimento,
  pesquisarProcessosComNotificacoes,
  pesquisarProcessosComAutorizacoes,
} from '../../../services/core/processos/processosService';
import {
  setLoadingAction,
  setResultadoAction,
  setErroAction,
} from './pesquisaProcessosUnificada.store';

const PESQUISAR_PROCESSOS_UNIFICADA = 'PESQUISAR_PROCESSOS_UNIFICADA';
const PESQUISAR_PROCESSOS_ATALHO = 'PESQUISAR_PROCESSOS_ATALHO';

/**
 * `page=0` fixo + tamanho alto para aproximar o comportamento "sem paginação
 * visível" que o endpoint unificado já tem — os 12 endpoints legados abaixo
 * são paginados no servidor (produção usa pageSize=15), diferente do
 * endpoint unificado. Validar contra volume real de dados (ver plano AW2-913
 * "Migrar navegação de pesquisa de processos do protótipo", Etapa 1).
 */
const TAMANHO_PAGINA_ATALHO = 500;

/**
 * Cada função já existe em `processosService.js` e já é usada hoje pela
 * tela real de produção (`pesquisarProcessos.controller.jsx`) — nenhum
 * endpoint novo é criado aqui, só o wiring do lado do protótipo.
 *
 * As 6 chaves com constante em `DASHBOARD_ACOES` a reaproveitam (mesmos
 * valores já usados pelos cards "Minhas Informações" de
 * `PesquisaProcessosPage.jsx`) — evita duas fontes de verdade para o mesmo
 * domínio. As demais não têm constante existente no projeto.
 * `notificacao`/`autorizacao` (singular, sem prefixo `processos`) espelham
 * os nomes dos query params reais (`?notificacao=true`/`?autorizacao=true`),
 * não o nome da função de service — nomenclatura intencionalmente diferente
 * das outras 10 chaves.
 */
const SERVICE_POR_TIPO_ATALHO = {
  [DASHBOARD_ACOES.MEUS_PROCESSOS]: pesquisarMeusProcessos,
  [DASHBOARD_ACOES.PROMESSAS_BOLETOS]: pesquisarPromessasBoletos,
  [DASHBOARD_ACOES.ACORDOS_NAO_CUMPRIDOS]: pesquisarAcordosNaoCumpridos,
  [DASHBOARD_ACOES.PRAZOS]: pesquisarPrazos,
  [DASHBOARD_ACOES.PROMESSAS_RETORNO]: pesquisarPromessasRetorno,
  [DASHBOARD_ACOES.PROCESSOS_NOVOS]: pesquisarProcessosNovos,
  processosAVencer: pesquisarProcessosAVencer,
  processosComPrazoVencido: pesquisarProcessosComPrazoVencido,
  processosAguardandoRequisicao: pesquisarProcessosAguardandoRequisicao,
  processosProximoVencimento: pesquisarProcessosProximoVencimento,
  notificacao: pesquisarProcessosComNotificacoes,
  autorizacao: pesquisarProcessosComAutorizacoes,
};

export const pesquisarProcessosUnificadaAction = payload => ({
  type: PESQUISAR_PROCESSOS_UNIFICADA,
  payload,
});

/**
 * @param {string} tipo - `'numeroProcesso'` (reaproveita o endpoint
 *   unificado com filtro) ou uma das chaves de `SERVICE_POR_TIPO_ATALHO`
 *   (endpoints legados dedicados).
 * @param {{numeroProcesso?: string}} [filtroExtra] - só usado quando
 *   `tipo === 'numeroProcesso'`.
 */
export const pesquisarAtalhoAction = (tipo, filtroExtra) => ({
  type: PESQUISAR_PROCESSOS_ATALHO,
  tipo,
  filtroExtra,
});

function* pesquisarProcessosUnificadaHandler(actions) {
  yield put(setLoadingAction(true));
  yield put(setErroAction(null));
  try {
    const { data } = yield call(pesquisarProcessosUnificadaService, actions.payload);
    yield put(setResultadoAction(data));
  } catch (exception) {
    yield put(setErroAction(getExceptionMessage(exception) || 'Erro ao pesquisar processos.'));
  } finally {
    yield put(setLoadingAction(false));
  }
}

function* pesquisarPorNumeroProcessoAtalho(filtroExtra) {
  const { data } = yield call(pesquisarProcessosUnificadaService, {
    tipoPesquisa: 'PADRAO',
    idPesquisaPronta: null,
    filtros: { numeroProcesso: filtroExtra?.numeroProcesso },
    sort: ['nroProcesso,ASC'],
  });
  yield put(setResultadoAction(data));
}

function* pesquisarPorTipoLegado(tipo) {
  const temServicoParaTipo = Object.prototype.hasOwnProperty.call(SERVICE_POR_TIPO_ATALHO, tipo);
  if (!temServicoParaTipo) {
    yield put(setErroAction('Tipo de pesquisa não reconhecido.'));
    return;
  }
  const service = SERVICE_POR_TIPO_ATALHO[tipo];
  const { data } = yield call(service, 0, TAMANHO_PAGINA_ATALHO, []);
  yield put(setResultadoAction(data?.content || []));
}

function* pesquisarAtalhoHandler(actions) {
  yield put(setLoadingAction(true));
  yield put(setErroAction(null));
  try {
    if (actions.tipo === 'numeroProcesso') {
      yield call(pesquisarPorNumeroProcessoAtalho, actions.filtroExtra);
    } else {
      yield call(pesquisarPorTipoLegado, actions.tipo);
    }
  } catch (exception) {
    yield put(setErroAction(getExceptionMessage(exception) || 'Erro ao pesquisar processos.'));
  } finally {
    yield put(setLoadingAction(false));
  }
}

export default function* watchPesquisaProcessosUnificada() {
  yield takeLatest(PESQUISAR_PROCESSOS_UNIFICADA, pesquisarProcessosUnificadaHandler);
  yield takeLatest(PESQUISAR_PROCESSOS_ATALHO, pesquisarAtalhoHandler);
}
